import React, { useState, useEffect, useCallback } from 'react';
import FlyerViewer from '../../components/FlyerViewer';
import Seo from '../../components/Seo';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  MapPin, Search as SearchIcon, GraduationCap, Clock, Calendar, Star, ArrowRight,
  Heart, ShieldCheck, Building2, Users, Sparkles, FileText, Handshake, TrendingUp,
} from 'lucide-react';
import { US_STATES, SEMESTERS, DEGREE_LEVELS, CONCENTRATIONS, FORMATS } from './constants';
import { useJsonLd, SITE_URL } from './useJsonLd';

const InternshipDirectory = () => {
  const [internships, setInternships] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [internStats, setInternStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/content-stats`).then((r) => setInternStats(r.data.internships_total ?? 0)).catch(() => {});
  }, []);
  const [filters, setFilters] = useState({ degree_level: '', concentration: '', state: '', format: '', semester: '', search: '' });
  const [draft, setDraft] = useState(filters);

  useJsonLd('internship-directory-schema', {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Social Work Internships & Field Placements',
    description: 'Directory of BSW and MSW social work internships and field placements across the United States.',
    url: `${SITE_URL}/internships`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: internships.slice(0, 25).map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_URL}/internships/${it.internship_id}`,
        name: `${it.title} — ${it.org_name}`,
      })),
    },
  });

  const fetchInternships = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API_URL}/internships`, { params });
      setInternships(res.data.internships || []);
      setTotal(res.data.total || 0);
    } catch (e) { setInternships([]); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchInternships(); }, [fetchInternships]);

  const setD = (k, v) => setDraft((f) => ({ ...f, [k]: v }));
  const applySearch = () => setFilters(draft);
  const clearAll = () => { const e = { degree_level: '', concentration: '', state: '', format: '', semester: '', search: '' }; setDraft(e); setFilters(e); };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        exactTitle="Social Work Internships Nationwide | BSW & MSW Field Placements | INASW"
        description="Find BSW and MSW social work internships, supervised field placements, and practicum opportunities nationwide. Explore opportunities on I Need A Social Worker."
        path="/internships"
      />
      {/* Photo hero banner (integrated, with overlay) */}
      <section className="relative pt-16">
        <div className="relative h-72 sm:h-80 lg:h-96 w-full overflow-hidden">
          <img src="/intern-hero-photo.png" alt="Social work intern working with students in a classroom" className="w-full h-full object-cover object-center" data-testid="internship-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/60 to-primary/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-xl text-white">
                <h1 className="font-serif text-4xl sm:text-5xl font-bold" data-testid="internship-directory-title">Find an Internship</h1>
                <p className="text-white/90 mt-3 text-base sm:text-lg">Discover verified BSW & MSW field placements. Gain experience, build skills, make a difference.</p>
                <Link to="/post-an-internship">
                  <Button className="mt-5 bg-secondary hover:bg-secondary-hover text-white" data-testid="post-internship-cta">Post an Internship</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INASW Social Work Interns flyer */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="text-center mb-3">
          <span className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-4 py-1.5 text-sm font-semibold" data-testid="internship-count-badge">
            {internStats !== null ? internStats.toLocaleString() : '—'} internship opportunities
          </span>
          <p className="text-xs text-gray-400 mt-1">New opportunities added automatically every morning at 8&nbsp;AM</p>
        </div>
        <FlyerViewer src="/flyer-social-work-intern.png" alt="INASW Social Work Interns" />
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-2xl border p-5 sticky top-24">
            <h2 className="font-serif text-lg font-bold text-primary mb-4">Search Internships</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Keyword</label>
                <Input placeholder="Title or organization" value={draft.search} onChange={(e) => setD('search', e.target.value)} data-testid="internship-search-input" />
              </div>
              <FilterSelect label="Degree Level" value={draft.degree_level} onChange={(v) => setD('degree_level', v)} options={DEGREE_LEVELS} testid="filter-degree" />
              <FilterSelect label="Concentration" value={draft.concentration} onChange={(v) => setD('concentration', v)} options={CONCENTRATIONS} testid="filter-concentration" />
              <FilterSelect label="State" value={draft.state} onChange={(v) => setD('state', v)} options={US_STATES} testid="filter-state" />
              <FilterSelect label="Format" value={draft.format} onChange={(v) => setD('format', v)} options={FORMATS} testid="filter-format" />
              <FilterSelect label="Semester" value={draft.semester} onChange={(v) => setD('semester', v)} options={SEMESTERS} testid="filter-semester" />
              <Button className="w-full bg-primary hover:bg-primary/90 text-white" onClick={applySearch} data-testid="search-internships-btn"><SearchIcon className="w-4 h-4 mr-2" /> Search Internships</Button>
              <button className="w-full text-center text-secondary text-sm font-medium hover:underline" onClick={clearAll} data-testid="clear-filters-btn">Clear Filters</button>
            </div>
            <p className="text-xs text-gray-400 mt-4 border-t pt-3">You must create a free account to apply to internships. All listings are reviewed for quality.</p>
          </div>
        </aside>

        <main className="lg:col-span-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-bold text-primary">Internship Opportunities</h2>
            <span className="text-sm text-gray-500" data-testid="internship-count">{loading ? 'Loading…' : `${total} posted`}</span>
          </div>
          {!loading && internships.length === 0 && (
            <div className="bg-white rounded-2xl border p-10 text-center" data-testid="no-internships">
              <GraduationCap className="w-10 h-10 text-secondary mx-auto mb-3" />
              <h3 className="font-serif text-xl text-primary font-bold mb-1">No internships match your filters</h3>
              <p className="text-gray-500">New field placements are added regularly — check back soon.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {internships.map((it) => <InternshipCard key={it.internship_id} it={it} />)}
          </div>
        </main>

        <aside className="lg:col-span-3">
          <div className="bg-white rounded-2xl border p-6 sticky top-24">
            <h3 className="font-serif text-xl font-bold text-primary mb-5">What to Look For</h3>
            <div className="space-y-4 text-sm text-gray-600">
              {['Meaningful experience', 'Supportive field instructors', 'Flexible schedule options', 'Diverse populations', 'Skill development', 'A pathway to your future'].map((t) => (
                <div key={t} className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-secondary flex-shrink-0" /> {t}</div>
              ))}
            </div>
            <div className="mt-6 border-t pt-5">
              <p className="font-serif text-lg font-bold text-primary mb-1">Organizations</p>
              <p className="text-sm text-gray-500 mb-3">Reach motivated students and build future social workers.</p>
              <Link to="/post-an-internship"><Button className="w-full bg-secondary hover:bg-secondary-hover text-white" data-testid="sidebar-post-internship">Post an Internship</Button></Link>
            </div>
          </div>
        </aside>
      </div>

      <section className="bg-white border-y py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-primary text-center mb-8">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            <Step icon={SearchIcon} num="1" title="Search" text="Filter and find internships that match your goals." />
            <Step icon={FileText} num="2" title="Apply" text="Submit your application easily through our platform." />
            <Step icon={Handshake} num="3" title="Connect" text="Organizations review applications and reach out." />
            <Step icon={TrendingUp} num="4" title="Grow" text="Gain experience, build skills, and advance your career." />
          </div>
        </div>
      </section>

      <section className="bg-primary py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-6 justify-between text-white">
          <div className="flex items-center gap-3">
            <Heart className="w-9 h-9 text-secondary" />
            <div><p className="font-serif text-xl font-bold">Your future starts with experience.</p><p className="text-white/80 text-sm italic">Find your internship today.</p></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TrustItem icon={ShieldCheck} title="Verified Opportunities" text="Listings reviewed for quality." />
            <TrustItem icon={Building2} title="Trusted Organizations" text="Reputable agencies and programs." />
          </div>
        </div>
      </section>
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={`Any ${label.toLowerCase()}`} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any {label.toLowerCase()}</SelectItem>
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const lab = typeof o === 'string' ? o : o.label;
          return <SelectItem key={val} value={val}>{lab}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  </div>
);

const formatLabel = (f) => (FORMATS.find((x) => x.value === f)?.label || f);

export const InternshipCard = ({ it }) => (
  <Link to={`/internships/${it.internship_id}`} className="bg-white rounded-2xl border p-5 hover:shadow-md hover:border-secondary/50 transition-all block" data-testid={`internship-card-${it.internship_id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0"><GraduationCap className="w-5 h-5" /></div>
      {it.is_featured && <Badge className="bg-secondary text-white text-xs flex items-center gap-1"><Star className="w-3 h-3" /> Featured</Badge>}
      {!it.is_featured && it.is_external && <Badge variant="outline" className="text-[10px] text-gray-500">Aggregated</Badge>}
    </div>
    <h3 className="font-serif text-lg font-bold text-primary mt-3">{it.title}</h3>
    <p className="text-sm text-gray-600">{it.org_name}</p>
    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {it.location} • {formatLabel(it.format)}</p>
    <div className="flex flex-wrap gap-1.5 mt-3">
      <Badge variant="outline" className="text-xs">{it.degree_level}</Badge>
      <Badge variant="outline" className="text-xs">{it.concentration}</Badge>
    </div>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-3">
      {it.start_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Start: {it.start_date}</span>}
      {it.weekly_hours && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {it.weekly_hours}</span>}
    </div>
    <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary">View details <ArrowRight className="w-4 h-4" /></div>
  </Link>
);

const Step = ({ icon: Icon, num, title, text }) => (
  <div className="text-center">
    <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-3"><Icon className="w-5 h-5" /></div>
    <p className="font-semibold text-primary">{num}. {title}</p>
    <p className="text-sm text-gray-500 mt-1">{text}</p>
  </div>
);

const TrustItem = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-6 h-6 text-secondary flex-shrink-0" />
    <div><p className="font-semibold">{title}</p><p className="text-white/70 text-sm">{text}</p></div>
  </div>
);

export default InternshipDirectory;
